<template>
	<div class="vdp-datepicker" :class="[wrapperClass, isRtl ? 'rtl' : '']" :style="{'right': `${rightPosition}px`}">
		<div class="absolute-wrapper" ref="datepickerElement">
			<!-- Day View -->
			<picker-day
			v-if="methods.allowedToShowView('day')"
			:pageDate="pageDate"
			:selectedDate="selectedDate"
			:showDayView="showDayView"
			:fullMonthName="fullMonthName"
			:allowedToShowView="methods.allowedToShowView"
			:disabledDates="disabledDates"
			:highlighted="highlighted"
			:calendarClass="calendarClass"
			:calendarStyle="calendarStyle"
			:translation="translation"
			:pageTimestamp="pageTimestamp"
			:isRtl="isRtl"
			:mondayFirst="mondayFirst"
			:dayCellContent="dayCellContent"
			:use-utc="useUtc"
			:showCancelButton="showCancelButton"
			:cancelButtonText="cancelButtonText"
			:rightArrowIcon="rightArrowIcon"
			:leftArrowIcon="leftArrowIcon"
			:showOtherMonthDays="showOtherMonthDays"
			:setBackgroundOnWeekend="setBackgroundOnWeekend"
			:monthOnTheRight="monthOnTheRight"
            :displayWeekDays="displayWeekDays"
            :disableWeekends="disableWeekends"
			@changedMonth="methods.handleChangedMonthFromDayPicker"
			@selectDate="methods.selectDate"
			@selectedDisabled="methods.selectDisabledDate"
			@closeCalendar="methods.close"
			@clearDate="methods.clearDate">
				<slot name="beforeCalendarHeader"></slot>
			</picker-day>
		</div>
	</div>
</template>

<script setup>
import { ref, computed, onMounted, watch, watchEffect } from 'vue'

import pt from '@/assets/localPlugins/datepicker/locale/translations/pt-BR'
import PickerDay from '@/assets/localPlugins/datepicker/PickerDay'
import utils, { makeDateUtils } from '@/assets/localPlugins/datepicker/utils/DateUtils'

/**
 * *************** EMITS ***************
*/

const emit = defineEmits([
    'selected',
    'updateDate',
    'selectedDisabled',
    'changedMonth',
    'changedYear',
    'cleared',
    'close'
])
/**
 * *************** PROPS ***************
*/
const props = defineProps({
    date: {
        validator: val => utils.validateDateInput(val)
    },
    name: String,
    refName: String,
    id: String,
    format: {
        type: [String, Function]
    },
    language: {
        type: Object,
        default: () => pt
    },
    openDate: {
        validator: val => utils.validateDateInput(val)
    },
    dayCellContent: Function,
    fullMonthName: {
        type: Boolean,
        default: () => true
    },
    disabledDates: Object,
    highlighted: Object,
    placeholder: String,
    inline: Boolean,
    calendarClass: [String, Object, Array],
    inputClass: [String, Object, Array],
    wrapperClass: [String, Object, Array],
    mondayFirst: Boolean,
    clearButton: Boolean,
    clearButtonIcon: String,
    calendarButton: Boolean,
    calendarButtonIcon: String,
    calendarButtonIconContent: String,
    bootstrapStyling: Boolean,
    initialView: String,
    disabled: Boolean,
    required: Boolean,
    typeable: Boolean,
    useUtc: Boolean,
    minimumView: {
        type: String,
        default: 'day'
    },
    maximumView: {
        type: String,
        default: 'year'
    },
    /* added props */
    showCancelButton: Boolean,
    cancelButtonText: String,
    rightArrowIcon: String,
    leftArrowIcon: String,
    showOtherMonthDays: Boolean,
    setBackgroundOnWeekend: Boolean,
    monthOnTheRight: Boolean,
    rightPosition: {
        type: Number,
        default: 0
    },
    displayWeekDays: {
        type: Boolean,
        default: true
    },
    disableWeekends: Boolean
})

/**
 * *************** DATA ***************
 */
const startDate = props.openDate ? new Date(props.openDate) : new Date()
const constructedDateUtils = computed(() => makeDateUtils(props.useUtc))
/*
* Vue cannot observe changes to a Date Object so date must be stored as a timestamp
* This represents the first day of the current viewing month
* {Number}
*/
const pageTimestamp = ref(constructedDateUtils.value.setDate(startDate, 1))
/*
* Selected Date
* {Date}
*/
const selectedDate = ref(null)
/*
* Flags to show calendar views
* {Boolean}
*/
const showDayView = ref(false)
const showMonthView = ref(false)
const showYearView = ref(false)
/*
* Positioning
*/
const calendarHeight = ref(0)
const resetTypedDate = new Date()

/**
 * *************** COMPUTED ***************
 */
const computedInitialView = computed(() => {
    return props.initialView ?? props.minimumView
})
const pageDate = computed(() => {
    return new Date(pageTimestamp.value)
})
const translation = computed(() => {
    return props.language
})
const calendarStyle = computed(() => {
    return {
        position: isInline.value ? 'static' : undefined
    }
})
const isOpen = computed(() => {
    return showDayView.value || showMonthView.value || showYearView.value
})
const isInline = computed(() => {
    return !!props.inline
})
const isRtl = computed(() => {
    return translation.value.rtl === true
})
/**
 * *************** METHODS ***************
 */
const methods = {
    /**
    * Called in the event that the user navigates to date pages and
    * closes the picker without selecting a date.
    */
    resetDefaultPageDate () {
        if (selectedDate.value === null) {
            methods.setPageDate()
            return
        }
        methods.setPageDate(selectedDate.value)
    },
    /**
    * Effectively a toggle to show/hide the calendar
    * @return {mixed}
    */
    showCalendar () {
        if (props.disabled || isInline.value) {
            return false
        }
        if (isOpen.value) {
            return methods.close(true)
        }
        methods.setInitialView()
    },
    /**
    * Sets the initial picker page view: day, month or year
    */
    setInitialView () {
        const initialView = computedInitialView.value
        if (!methods.allowedToShowView(initialView)) {
            throw new Error(`initialView '${initialView}' cannot be rendered based on minimum '${props.minimumView}' and maximum '${props.maximumView}'`)
        }
        switch (props.initialView) {
        case 'year':
            methods.showYearCalendar()
            break
        case 'month':
            methods.showMonthCalendar()
            break
        default:
            methods.showDayCalendar()
            break
        }
    },
    /**
    * Are we allowed to show a specific picker view?
    * @param {String} view
    * @return {Boolean}
    */
    allowedToShowView (view) {
        const views = ['day', 'month', 'year']
        const minimumViewIndex = views.indexOf(props.minimumView)
        const maximumViewIndex = views.indexOf(props.maximumView)
        const viewIndex = views.indexOf(view)

        return viewIndex >= minimumViewIndex && viewIndex <= maximumViewIndex
    },
    /**
    * Show the day picker
    * @return {Boolean}
    */
    showDayCalendar () {
        if (!methods.allowedToShowView('day')) {
            return false
        }
        methods.close()
        showDayView.value = true
        return true
    },
    /**
    * Set the selected date
    * @param {Number} timestamp
    */
    setDate (timestamp) {
        const date = new Date(timestamp)
        selectedDate.value = date
        methods.setPageDate(date)
        emit('selected', date)
        emit('updateDate', date)
    },
    /**
    * Clear the selected date
    */
    clearDate () {
        emit('close')
        // selectedDate.value = null
        // methods.setPageDate()
        // emit('selected', null)
        // emit('updateDate', null)
        // emit('cleared')
    },
    /**
    * @param {Object} date
    */
    selectDate (date) {
        methods.setDate(date.timestamp)
        if (!isInline.value) {
            methods.close(true)
        }
        resetTypedDate.value = new Date()
        emit('updateDate', date)
    },
    /**
    * @param {Object} date
    */
    selectDisabledDate (date) {
        emit('selectedDisabled', date)
    },
    /**
    * @param {Object} month
    */
    selectMonth (month) {
        const date = new Date(month.timestamp)
        if (methods.allowedToShowView('day')) {
            methods.setPageDate(date)
            emit('changedMonth', month)
            methods.showDayCalendar()
        } else {
            methods.selectDate(month)
        }
    },
    /**
    * @param {Object} year
    */
    selectYear (year) {
        const date = new Date(year.timestamp)
        if (methods.allowedToShowView('month')) {
            methods.setPageDate(date)
            emit('changedYear', year)
            methods.showMonthCalendar()
        } else {
            methods.selectDate(year)
        }
    },
    /**
    * Set the datepicker value
    * @param {Date|String|Number|null} date
    */
    setValue (date) {
        if (typeof date === 'string' || typeof date === 'number') {
            const parsed = new Date(date)
            date = isNaN(parsed.valueOf()) ? null : parsed
        }
        if (!date) {
            methods.setPageDate()
            selectedDate.value = null
            return
        }
        selectedDate.value = date
        methods.setPageDate(date)
    },
    /**
    * Sets the date that the calendar should open on
    */
    setPageDate (date) {
        if (!date) {
            if (props.openDate) {
                date = new Date(props.openDate)
            } else {
                date = new Date()
            }
        }
        pageTimestamp.value = constructedDateUtils.value.setDate(new Date(date), 1)
    },
    /**
    * Handles a month change from the day picker
    */
    handleChangedMonthFromDayPicker (date) {
        methods.setPageDate(date)
        emit('changedMonth', date)
    },
    /**
    * Set the date from a typedDate event
    */
    setTypedDate (date) {
        methods.setDate(date.getTime())
    },
    /**
    * Close all calendar layers
    * @param {Boolean} emitEvent - emit close event
    */
    close (emitEvent) {},
    /**
    * Initiate the component
    */
    init () {
        if (props.date) {
            methods.setValue(props.date)
        }
        if (isInline.value) {
            methods.setInitialView()
        }
        methods.showCalendar()
    }
}

/**
 * *************** WATCH EFFECTS ***************
 */
watchEffect(() => {
    methods.setValue(props.date)
})
watch(() => props.openDate, () => {
    methods.setPageDate()
})
watch(() => props.initialView, () => {
    methods.setInitialView()
})
/**
 * *************** HOOKS ***************
 */
onMounted(() => {
    methods.init()
})

/** REFS */
const datepickerElement = ref(null)
</script>

<style lang="scss">
</style>
